/**
 * ═══════════════════════════════════════════════════════════════════════════
 * Pagination & Limits — shared resolver
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Every page/grid that reads a product-listing limit from AnimationSettings
 * (via `infoSource.allSettings`) used to duplicate the same inline fallback
 * check: `Number.isFinite(x) && x > 0 ? x : someDefault`. This file is the
 * single place that logic lives now — pages call one of the helpers below
 * instead of re-deriving it.
 *
 * Two namespaces exist in AnimationSettings for this (unchanged, not
 * renamed — see AnimationSettings.jsx's PAGINATION_LIMITS_DEFAULTS /
 * PRODUCT_GRID_PAGE_KEYS):
 *   - settings.paginationLimits.<key>            (home, userView,
 *     searchHistoryAllCards, viewingActivityAllCards, shopsYouFollowAllCards,
 *     homeSliders.*)
 *   - settings.productGrids.<bucket>.limit       (productGridPage,
 *     productGridShop, productGridSearch, ordersGridPage)
 *
 * IMPORTANT — this module never reads or writes `skip`/`page`/`offset`.
 * Those are always runtime values derived from the resolved limit, never a
 * saved AnimationSettings field (see calculatePaginationOffset below).
 */

/**
 * Normalize a configured limit value to a safe positive integer, falling
 * back to `fallback` for anything that isn't a finite number > 0 (missing
 * setting, 0, negative, NaN, a non-numeric string, null/undefined...).
 * Never mutates its input.
 */
export const resolvePaginationLimit = (configuredValue, fallback) => {
  const parsed = Number(configuredValue);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

/**
 * Read `paginationLimits.<key>` from `allSettings` with a safe fallback.
 * Used by: home.jsx ('home'), userViewed.js ('userView'),
 * searchHistoryAllCards.js / viewingActivityAllCards.js /
 * shopsYouFollowAllCards.js (their own PAGE_ID), MatrixCardsGrid.jsx
 * (per-facet key).
 */
export const getPaginationLimit = (allSettings, key, fallback) =>
  resolvePaginationLimit(allSettings?.paginationLimits?.[key], fallback);

/**
 * Read `productGrids.<bucket>.limit` from `allSettings` with a safe
 * fallback. Used by ProductGridPage.jsx / ProductGridShop.jsx /
 * ProductGridSearch.jsx, and — now — Best Sellers / Deal of the Day /
 * Product Catalog's own facet-batched loader, which intentionally share the
 * same 'productGridPage' bucket as New Products and Product Catalog's real
 * grid (see project note: Main Pages & Category Pages coordination).
 */
export const getProductGridLimit = (allSettings, bucket = 'productGridPage', fallback = 12) =>
  resolvePaginationLimit(allSettings?.productGrids?.[bucket]?.limit, fallback);

/**
 * currentPage/limit → skip, the one calculation every page should use
 * instead of re-deriving it. `skip` is never persisted (see module doc) —
 * it is always computed at call time from a 1-based currentPage and the
 * already-resolved limit.
 */
export const calculatePaginationOffset = (currentPage, limit, fallbackLimit = 12) => {
  const safeLimit = resolvePaginationLimit(limit, fallbackLimit);
  const parsedPage = Number(currentPage);
  const safePage = Number.isFinite(parsedPage) && parsedPage > 0 ? parsedPage : 1;
  return (safePage - 1) * safeLimit;
};
